import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'

import ImpressionsGraph from '../../../../../features/components/SummaryGraphs/ImpressionsGraph'
import ClicksGraph from '../../../../../features/components/SummaryGraphs/ClicksGraph'
import CTRGraph from '../../../../../features/components/SummaryGraphs/CTRGraph'
import GenderGraphSection from '../../../../../features/components/SummaryGraphs/GenderGraphSection'
import AgeGraphSection from '../../../../../features/components/SummaryGraphs/AgeGraphSection'
import PlatformGraphSection from '../../../../../features/components/SummaryGraphs/PlatformGraphSection'
import SkeletonSummaryGraphs from '../../../../../features/components/SummaryGraphs/Skeleton'

import { useGraphData } from '../../../../../features/components/SummaryGraphs/useGraphData'
import { getFormattedCombinedGraphs } from './helpers'
import { groupCampaignsByProvider } from '../../RightCampaigns/helpers'

import {
  clearCombinedGraphs,
  clearCombinedSegmentedData,
  getCombinedGraphs,
  getCombinedSegmentedData
} from '../../../../../modules/actions/combinedData'
import {
  combinedCampaignsSelector,
  combinedGraphsIsLoadingSelector,
  combinedGraphsSelector,
  combinedSegmentedDataIsLoadingSelector,
  combinedSegmentedDataSelector
} from '../../../../../modules/selectors/combinedData'
import { rightSelector } from '../../../../../modules/selectors/rights'
import { selectedAdAccountsSelector } from '../../../../../modules/selectors/adAccounts'

import { DATES_FORMAT_BE } from '../../../../../constants/dates'
import { FACEBOOK_PLATFORM } from '../../../../../constants/selectLists/platformList'

import useGraphsSummaryStyles from '../../../../../features/components/SummaryGraphs/styles'

const Graphs = () => {
  const graphsSummaryClasses = useGraphsSummaryStyles()

  const dispatch = useDispatch()

  const combinedGraphs = useSelector(combinedGraphsSelector)
  const combinedGraphsIsLoading = useSelector(combinedGraphsIsLoadingSelector)
  const combinedSegmentedData = useSelector(combinedSegmentedDataSelector)
  const combinedSegmentedDataIsLoading = useSelector(combinedSegmentedDataIsLoadingSelector)
  const { campaigns_date_range: campaignsDateRange } = useSelector(combinedCampaignsSelector)
  const { [FACEBOOK_PLATFORM]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)
  const right = useSelector(rightSelector)

  const { date_from: campaignsDateStart, date_to: campaignsDateEnd } = campaignsDateRange

  // todo think which platform to use to get currency symbol, currently using facebook, actually it is same for all platforms in one partnership
  const { currency_symbol: currencySymbol } = selectedAdAccount
  const { campaigns: rightCampaignsToLoad } = right

  const graphsIsLoading = combinedGraphsIsLoading || combinedSegmentedDataIsLoading

  const campaignsGroupedByProvider = useMemo(
    () => groupCampaignsByProvider(rightCampaignsToLoad),
    [rightCampaignsToLoad]
  )
  const { impressionsData, ctrData, clicksData } = useGraphData(combinedGraphs)

  const formattedAgeData = useMemo(
    () => getFormattedCombinedGraphs(combinedSegmentedData?.age_data),
    [combinedSegmentedData?.age_data]
  )
  const formattedGenderData = useMemo(
    () => getFormattedCombinedGraphs(combinedSegmentedData?.gender_data),
    [combinedSegmentedData?.gender_data]
  )
  const formattedPlatformData = useMemo(
    () =>
      getFormattedCombinedGraphs(combinedSegmentedData?.publisher_platform_data).map(item => {
        console.log('item', item)

        if (item.breakdown === 'facebook') {
          return {
            ...item,
            // On right summary page reports we need to change 'facebook' platform name to 'Meta'
            breakdown: 'Meta'
          }
        } else {
          return item
        }
      }),
    [combinedSegmentedData?.publisher_platform_data]
  )

  const sortedPlatformData = useMemo(
    () => formattedPlatformData?.length && [...formattedPlatformData].sort((a, b) => b.percent - a.percent),
    [formattedPlatformData]
  )

  useEffect(() => {
    dispatch(
      getCombinedGraphs({
        insights: ['impressions', 'cpm', 'clicks', 'ctr', 'conversions', 'engagement', 'spend'],
        ...campaignsDateRange,
        providers: campaignsGroupedByProvider
      })
    )
  }, [dispatch, campaignsDateRange, campaignsGroupedByProvider])

  useEffect(() => {
    dispatch(
      getCombinedSegmentedData({
        insights: ['impressions', 'cpm', 'clicks', 'ctr', 'conversions'],
        breakdowns: ['gender', 'age', 'publisher_platform'],
        // segmentedData endpoint doesn't support date_preset field, and requires date_from and date_to
        date_from: format(new Date(campaignsDateStart), DATES_FORMAT_BE),
        date_to: format(new Date(campaignsDateEnd), DATES_FORMAT_BE),
        providers: campaignsGroupedByProvider
      })
    )
  }, [dispatch, campaignsDateStart, campaignsDateEnd, campaignsGroupedByProvider])

  useEffect(() => {
    return () => {
      dispatch(clearCombinedGraphs())
      dispatch(clearCombinedSegmentedData())
    }
  }, [dispatch])

  return (
    <section id="graphs-report">
      {graphsIsLoading ? (
        <SkeletonSummaryGraphs />
      ) : (
        <div className={graphsSummaryClasses.graphsContainer}>
          {impressionsData && <ImpressionsGraph rawGraphData={impressionsData} />}
          {clicksData && <ClicksGraph rawGraphData={clicksData} />}
          {ctrData && <CTRGraph rawGraphData={ctrData} />}
          {!!formattedGenderData?.length && (
            <GenderGraphSection rawGraphData={formattedGenderData} currencySymbol={currencySymbol} />
          )}
          {!!formattedAgeData?.length && (
            <AgeGraphSection rawGraphData={formattedAgeData} currencySymbol={currencySymbol} />
          )}
          {!!formattedPlatformData?.length && (
            <PlatformGraphSection
              rawGraphData={sortedPlatformData}
              currencySymbol={currencySymbol}
              title={'Ad Platform'}
            />
          )}
        </div>
      )}
    </section>
  )
}

export default Graphs
